import { Inject, Injectable } from '@angular/core';
import {
  DecodedIdToken,
  Hermes,
  HermesError,
  HermesOptions,
  HermesSession,
  HermesTokenResult,
  RedirectResult,
  RefreshTokenExchangeResult,
  Transaction,
} from '@kaligo/hermes';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  RdLibModuleCookiesConfig,
  RdLibModuleHermesModeConfig,
  RdLibModuleIdleCheckConfig,
} from '@rd-lib/rd-lib.module.config';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import {
  APP_ID,
  CONNECTION,
  COOKIES_CONFIG,
  GUARDHOUSE_API_URL,
  HERMES_MODE,
  IDLE_CHECK_CONFIG,
  REDIRECT_URI,
  SCOPE,
  SESSION_CHECK_POLL_INTERVAL,
} from '@rd/core/tokens/rd-lib.token';
import { HermesErrorCodes } from '@rd/errors/hermes-error-codes.type';
import { RewardsDashboardErrors } from '@rd/errors/rewards-dashboard-errors';
import { DialogRef } from '@rd/popup/dialog-ref';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { TransactionAppState, User, UserPreference } from '@rd/types';
import { EnvironmentConnection, StartsWithHttps } from 'global-types';
import { v4 as uuid } from 'uuid';
import {
  LoadIframeTokenSuccess,
  LoadRTRTokenSuccess,
  LoadTokenFail,
  LogoutUser,
  SessionCheckFail,
} from '../../auth/store/actions/auth.actions';
import { AuthState } from '../../auth/store/reducers/auth.reducer';
import { AuthUserIdleDialogComponent } from '../../containers/auth-user-idle-dialog/auth-user-idle-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class HermesService {
  private readonly hermes: Hermes;
  private dialogRef: DialogRef;

  constructor(
    @Inject(GUARDHOUSE_API_URL) authBaseUrl: StartsWithHttps,
    @Inject(SESSION_CHECK_POLL_INTERVAL) sessionCheckPollInterval: number,
    @Inject(IDLE_CHECK_CONFIG)
    idleCheckConfig: RdLibModuleIdleCheckConfig,
    @Inject(COOKIES_CONFIG) cookiesConfig: RdLibModuleCookiesConfig,
    @Inject(SCOPE) scope: string,
    @Inject(REDIRECT_URI) redirectUri: StartsWithHttps,
    @Inject(APP_ID) appId: string,
    @Inject(CONNECTION) private connection: EnvironmentConnection,
    @Inject(HERMES_MODE) hermesMode: RdLibModuleHermesModeConfig,
    private sentryService: SentryService,
    private dialogService: DialogService,
    private store: Store<AuthState>,
    private translateService: TranslateService,
  ) {
    const commonOptions: Partial<HermesOptions> = {
      authBaseUrl,
      sessionCheck: {
        pollInterval: sessionCheckPollInterval,
      },
      idleCheck: {
        onTimeout: () => this.onIdleTimeout(),
        onActivityRestored: () => this.onIdleActivityRestored(),
        timeout: idleCheckConfig.timeout,
        promptTimeout: idleCheckConfig.promptTimeout,
        dialogElementId: 'user-idle-dialog',
      },
      storagePrefix: 'rd.txs.',
    };

    if (hermesMode === 'iframe') {
      const options: HermesOptions = {
        ...commonOptions,
        mode: 'iframe',
      };

      this.hermes = new Hermes(options);
    } else if (hermesMode === 'refresh-token-rotation') {
      const options: HermesOptions = {
        ...commonOptions,
        mode: 'rtr',
        appId,
        redirectUri,
        scope,
      };

      this.hermes = new Hermes(options);
    }
  }

  private onIdleTimeout(): void {
    this.dialogRef = this.dialogService.open<{ hermes: Hermes }>(
      AuthUserIdleDialogComponent,
      {
        data: {
          hermes: this.hermes,
        },
        id: 'user-idle-dialog',
        screenMode: 'center-md',
        ariaLabel: this.translateService.instant(
          'accessibility.auth.idle_dialog',
        ),
      },
    );
  }

  private onIdleActivityRestored(): void {
    this.dialogRef.close();
  }

  startIdleCheck(): void {
    this.hermes.startIdleCheck();
  }

  async startIframeTokenRefresh(): Promise<void> {
    const tokens = this.hermes.startTokenRefresh();

    try {
      for await (const token of tokens) {
        if (token.accessToken && token.idToken) {
          this.store.dispatch(
            new LoadIframeTokenSuccess({ tokenResult: token }),
          );
        } else {
          throw RewardsDashboardErrors.TOKEN_UNDEFINED;
        }
      }
    } catch (error) {
      this.store.dispatch(new LoadTokenFail(error as HermesError));
    }
  }

  async startRTRTokenRefresh(
    transactionAppState: TransactionAppState,
  ): Promise<void> {
    const availableRefreshTokens = localStorage.getItem('refresh_token');
    const tokens = this.hermes.startRefreshTokenRotation();
    let includeTransaction = true;
    try {
      for await (const token of tokens) {
        if (token.accessToken && token.idToken) {
          // Only include the transactionAppState in the first loadTokenSuccess to avoid replaying the same action repeatedly
          const action = includeTransaction
            ? new LoadRTRTokenSuccess({
                tokenResult: token,
                transactionAppState,
              })
            : new LoadRTRTokenSuccess({ tokenResult: token });
          this.store.dispatch(action);
          includeTransaction = false;
        } else {
          throw RewardsDashboardErrors.TOKEN_UNDEFINED;
        }
      }
    } catch (error) {
      if ((error as HermesError).code === HermesErrorCodes.INVALID_TOKEN) {
        this.sentryService.manualWarning(
          'Hermes Invalid Token Error',
          availableRefreshTokens,
        );
        console.log('Hermes Invalid Token Error', availableRefreshTokens);
      }
      this.store.dispatch(new LoadTokenFail(error as HermesError));
    }
  }

  stopTokenRefresh(): void {
    this.hermes.stopTokenRefresh();
  }

  sessionCheck(): Promise<HermesSession> {
    return this.hermes.sessionCheck(uuid());
  }

  async startSessionCheck(): Promise<void> {
    const sessions = this.hermes.startSessionCheck();

    try {
      for await (const session of sessions) {
        if (!session.loggedIn) {
          this.store.dispatch(new LogoutUser());
        }
      }
    } catch (error) {
      this.store.dispatch(new SessionCheckFail(error as HermesError));
    }
  }

  stopSessionCheck(): void {
    this.hermes.stopSessionCheck();
  }

  parseToken(decodedIdToken: DecodedIdToken): User {
    return {
      sub: decodedIdToken.sub,
      uid: decodedIdToken.uid,
      act: decodedIdToken.act,
      nonce: decodedIdToken.nonce,
      firstName: decodedIdToken.first_name,
      lastName: decodedIdToken.last_name,
      email: decodedIdToken.email,
      salutation: decodedIdToken.salutation,
      picture: decodedIdToken.picture,
      name: decodedIdToken.name,
      middleName: decodedIdToken.middle_name,
      nickname: decodedIdToken.nickname,
      preferredUsername: decodedIdToken.preferred_username,
      profile: decodedIdToken.profile,
      gender: decodedIdToken.gender,
      ...(decodedIdToken.birthdate && {
        birthdate: new Date(decodedIdToken.birthdate),
      }),
      zoneinfo: decodedIdToken.zoneinfo,
      locale: decodedIdToken.locale,
      scopes: decodedIdToken.scope?.split(' '),
      tenantId: decodedIdToken.tenant_id,
      emailVerified: decodedIdToken.email_verified,
      address: decodedIdToken.address,
      phone: decodedIdToken.phone_number,
      phoneNumberVerified: decodedIdToken.phone_number_verified,
      country: decodedIdToken.country,
      custom: decodedIdToken.custom,
      status: decodedIdToken.status,
    };
  }

  parsePreferences(
    tokenResult: RefreshTokenExchangeResult | HermesTokenResult,
  ): UserPreference {
    const preferences = tokenResult.preferences;

    return {
      locale: preferences.locale,
      currency: preferences.currency,
      loyaltyCurrency: preferences.loyaltyCurrency,
      pointsAccountId: preferences.pointsAccountId,
    };
  }

  removeRefreshToken(): void {
    this.hermes.removeRefreshToken();
  }

  async handleRedirect(): Promise<TransactionAppState | null> {
    const result = await this.hermes.handleRedirect();
    return (
      (result as RedirectResult)?.transaction ||
      (result as Transaction)?.appState
    );
  }

  async login(transaction: Transaction): Promise<void> {
    await this.hermes.login(this.connection, transaction);
  }

  saveState(state: string, currentUrl?: string): void {
    const transaction: Transaction = { nonce: state };

    if (currentUrl) {
      transaction.appState = { currentUrl };
    }

    this.hermes.saveState(state, transaction);
  }

  saveStateWithData(nonce: string, appState: Record<string, any>): void {
    const transaction: Transaction = { nonce, appState };
    this.hermes.saveState(nonce, transaction);
  }
}
